import { ReactNode, useState } from 'react';

import { CheckCheck, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date-formatters';

import { Notification } from '../../types';

interface NotificationItemShellProps {
  notification: Notification;
  title: string;
  actionUrl: string | null;
  onMarkAsRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
  /** Content rendered below the title row — description text, extra controls, etc. */
  children: ReactNode;
}

/**
 * Shared shell for inbox notification items: title row (with hover-revealed
 * mark-as-read/delete buttons, or the timestamp + unread dot when not
 * hovering) and a click-to-navigate wrapper. Callers supply the body content
 * — description text, extra controls — as children.
 */
export function NotificationItemShell({
  notification,
  title,
  actionUrl,
  onMarkAsRead,
  onDelete,
  children,
}: NotificationItemShellProps) {
  const { t } = useTranslation('authenticated.notifications');
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);

  const handleMarkAsRead = () => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const handleDelete = () => {
    onDelete?.(notification.id);
  };

  const handleClick = () => {
    if (actionUrl) {
      if (!notification.isRead && onMarkAsRead) {
        onMarkAsRead(notification.id);
      }
      navigate(actionUrl);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 border-b border-border px-4 py-3',
        !notification.isRead && 'bg-muted/30',
        actionUrl && 'cursor-pointer hover:bg-muted/50'
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="flex-1 text-sm font-medium leading-tight text-foreground">{title}</h4>
        <div className="relative flex h-5 items-center gap-1">
          {isHovering ? (
            <>
              {!notification.isRead && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs text-foreground shadow-sm"
                  onClick={e => {
                    e.stopPropagation();
                    handleMarkAsRead();
                  }}
                >
                  <CheckCheck className="h-4 w-4" />
                  {t('inbox.markAsRead')}
                </button>
              )}
              <button
                type="button"
                className="flex items-center rounded-md border bg-background p-1 text-muted-foreground shadow-sm hover:text-destructive"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {formatRelativeTime(notification.updatedAt ?? notification.createdAt)}
              </span>
              {!notification.isRead && (
                <span className="ml-1.5 h-2 w-2 shrink-0 rounded-full bg-destructive" />
              )}
            </>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
